import React from 'react';
import { Alert, App, Button, Segmented, Typography } from 'antd';
import { AppstoreOutlined, CodeOutlined } from '@ant-design/icons';
import LessonBodyEditor from '../LessonBodyEditor';
import VisualBodyEditor from './VisualBodyEditor';
import { EditorErrorBoundary } from './EditorErrorBoundary';
import { BodyDraft, BodyMode, toJsonDraft, toVisualDraft } from './draft';

const { Text } = Typography;

/**
 * Tall enough to work in, short enough that the modal's footer stays on screen. Both modes
 * use it, so switching doesn't make the modal jump. The JSON editor's toolbar takes ~40px.
 */
const BODY_HEIGHT = 'max(460px, calc(100vh - 460px))';
const JSON_EDITOR_STYLE = { '--lbe-json-height': `calc(${BODY_HEIGHT} - 40px)` } as React.CSSProperties;

const MODE_OPTIONS = [
  { value: 'visual' as const, label: 'Visual', icon: <AppstoreOutlined /> },
  { value: 'json' as const, label: 'JSON', icon: <CodeOutlined /> },
];

interface Props {
  draft: BodyDraft;
  onChange: (draft: BodyDraft) => void;
  error: string | null;
  onErrorChange: (error: string | null) => void;
}

/** Lesson body with a Visual / JSON switch. Switching to Visual is refused while the JSON is invalid. */
const LessonBodyField: React.FC<Props> = ({ draft, onChange, error, onErrorChange }) => {
  const { message } = App.useApp();

  const switchMode = (mode: BodyMode) => {
    if (mode === draft.mode) return;
    if (mode === 'json') {
      onChange(toJsonDraft(draft));
      onErrorChange(null);
      return;
    }
    const result = toVisualDraft(draft);
    if ('error' in result) {
      onErrorChange(result.error);
      message.error('Fix the JSON before switching to the visual editor.');
      return;
    }
    onChange(result.draft);
    onErrorChange(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 12 }}>
          <Text strong>Body</Text>
          {draft.mode === 'visual' && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Select text and press Ctrl/⌘+B to make it <strong>bold</strong>
            </Text>
          )}
        </span>
        <Segmented<BodyMode> size="small" value={draft.mode} options={MODE_OPTIONS} onChange={switchMode} />
      </div>

      {draft.mode === 'visual' ? (
        <EditorErrorBoundary
          fallback={(err) => (
            <Alert
              type="error"
              showIcon
              title="The visual editor can't show this body"
              description={err.message}
              action={<Button size="small" onClick={() => switchMode('json')}>Edit as JSON</Button>}
            />
          )}
        >
          <VisualBodyEditor
            value={draft.body}
            onChange={(body) => onChange({ mode: 'visual', body })}
            style={{ height: BODY_HEIGHT }}
          />
        </EditorErrorBoundary>
      ) : (
        <div className="lbe-json" style={JSON_EDITOR_STYLE}>
          <LessonBodyEditor
            value={draft.text}
            onChange={(text) => {
              onChange({ mode: 'json', text });
              onErrorChange(null);
            }}
            error={error}
          />
        </div>
      )}
      {error && <Alert style={{ marginTop: 8 }} type="error" showIcon title={error} />}
    </div>
  );
};

export default LessonBodyField;
